import type { RailBranch } from "@/data/rail-branches";
import type { SceneBeat } from "@/lib/timeline/book/BookLayout";
import { locationRunsFromRail } from "./railRuns";

export { locationRunsFromRail };

export interface LocationRun {
  /** First scene in the run (stable DOM id). */
  runId: string;
  /** Outermost place. Rooms inside it share one run. */
  locationLabel: string;
  /** Inner place when every scene in the run is that same part. */
  nestedLabel?: string;
  startMs: number;
  endMs: number;
  /** Parallel line. 0 is the line the chapter starts on. */
  lane?: number;
}

function branchIdForRun(run: LocationRun): string {
  const colon = run.runId.indexOf(":");
  return colon === -1 ? run.runId : run.runId.slice(0, colon);
}

/** Map a scene or part run to the rail branch run for stack height and labels. */
export function matchingRailRun(
  run: LocationRun,
  branches: readonly RailBranch[],
  beats: readonly SceneBeat[],
): LocationRun {
  const branchKey = branchIdForRun(run);
  const direct = branches.find((branch) => branch.id === branchKey);
  if (direct !== undefined) {
    const railRuns = locationRunsFromRail(branches, beats);
    return railRuns.find((item) => item.runId === direct.id) ?? run;
  }
  if (branches.some((branch) => branch.id === run.runId)) {
    return run;
  }
  const railRuns = branches.length > 0
    ? locationRunsFromRail(branches, beats)
    : buildLocationRuns(beats);
  const byTime = railRuns.find(
    (item) =>
      run.startMs >= item.startMs
      && run.startMs < item.endMs
      && item.locationLabel === run.locationLabel,
  );
  if (byTime !== undefined) {
    return byTime;
  }
  return railRuns.find((item) => item.locationLabel === run.locationLabel) ?? run;
}

const runsByBeats = new WeakMap<readonly SceneBeat[], LocationRun[]>();

/** A loaded book's scenes never change, so its runs are built once. */
export function buildLocationRuns(beats: readonly SceneBeat[]): LocationRun[] {
  const cached = runsByBeats.get(beats);
  if (cached !== undefined) {
    return cached;
  }
  const runs = computeLocationRuns(beats);
  runsByBeats.set(beats, runs);
  return runs;
}

function computeLocationRuns(beats: readonly SceneBeat[]): LocationRun[] {
  const runs: LocationRun[] = [];
  let previous: SceneBeat | undefined;
  for (const beat of beats) {
    const path = locationPathOf(beat);
    const root = path[0] ?? beat.locationLabel;
    const nested = path.length > 1 ? path[path.length - 1] : undefined;
    const last = runs.at(-1);
    if (last !== undefined && last.locationLabel === root && sameChapter(previous, beat)) {
      last.endMs = beat.endMs;
      if (last.nestedLabel !== nested) {
        delete last.nestedLabel;
      }
      previous = beat;
      continue;
    }
    runs.push({
      runId: beat.sceneId,
      locationLabel: root,
      startMs: beat.startMs,
      endMs: beat.endMs,
      ...(nested !== undefined ? { nestedLabel: nested } : {}),
    });
    previous = beat;
  }
  return runs;
}

function sameChapter(previous: SceneBeat | undefined, beat: SceneBeat): boolean {
  if (previous?.chapterId === undefined || beat.chapterId === undefined) {
    return true;
  }
  return previous.chapterId === beat.chapterId;
}

export function locationPathOf(beat: SceneBeat): string[] {
  if (beat.locationPath !== undefined && beat.locationPath.length > 0) {
    return beat.locationPath;
  }
  return [beat.locationLabel];
}

/** Inner label under the site box. The active scene wins inside a mixed run. */
export function visibleNestedLabel(
  run: LocationRun,
  isActive: boolean,
  activePath: readonly string[],
): string | null {
  if (
    isActive &&
    activePath.length > 1 &&
    activePath[0] === run.locationLabel
  ) {
    return activePath[activePath.length - 1] ?? null;
  }
  return run.nestedLabel ?? null;
}

/** Run whose location label applies at `playheadMs` (inclusive start, exclusive end). */
export function activeLocationRun(
  runs: LocationRun[],
  playheadMs: number,
): LocationRun | null {
  if (runs.length === 0) {
    return null;
  }
  const active = runs.find(
    (run) => playheadMs >= run.startMs && playheadMs < run.endMs,
  );
  if (active !== undefined) {
    return active;
  }
  if (playheadMs < runs[0].startMs) {
    return runs[0];
  }
  return runs.at(-1) ?? null;
}
